import {h, Fragment} from "preact"
import { AutocompleteElement, useCurrencyFormatting } from "@nosto/preact"
import { useMemo, useState, useEffect } from "preact/hooks";
import { productImagePlaceholder, getDropdownSrcset, groupVariants, findSkuFromColor, getPriceRange } from "../helpers"
import { INITIAL_SWATCHES_SHOWN } from "../config"

export default function DropdownProduct({ product, colorInQuery }) {
    const { format } = useCurrencyFormatting()
    const skuByColor = useMemo(() => groupVariants(product, 'color'), [])
    const colors = useMemo(() => skuByColor ? Object.keys(skuByColor) : [], []);
    const badge = useMemo(() => product.customFields?.find(field => field.key === "cql-badge_label")?.value, [])

    const [ swatchesDisplayed, setSwatchesDisplayed ] = useState(INITIAL_SWATCHES_SHOWN);
    const [ currentColor, setCurrentColor ] = useState(colors[0]);
    const [ colorClicked, setColorClicked ] = useState(false);
    const [ images, setImages ] = useState({
        main: skuByColor[currentColor]?.imageUrl || product.imageUrl || null,
        alt: skuByColor[currentColor]?.altImage || product.alternateImageUrls?.[0] || null,
    });

    const priceRange = useMemo(() => getPriceRange(product), []); // avoids running function on state update
    const [ prices, setPrices ] = useState({
        price: skuByColor[currentColor]?.price || product.price, 
        listPrice: skuByColor[currentColor]?.listPrice || product.listPrice,
        range: priceRange,
    });

    useEffect(() => { // updates preselected swatch if color found in user search query
        if(!colorInQuery) return;
        const matchingSkuColor = findSkuFromColor(colorInQuery, skuByColor);
        if(matchingSkuColor !== currentColor) {
            setCurrentColor(matchingSkuColor)
        }
    }, [ colorInQuery, skuByColor ])

    useEffect(() => { // updates price when swatch changes
        if(colorClicked && currentColor && skuByColor[currentColor]) {
            setPrices({
                ...prices,
                price: skuByColor[currentColor]?.price,
                listPrice: skuByColor[currentColor]?.listPrice,
            });
        }
    }, [ currentColor, colorClicked ])

    useEffect(() => { // updates images when swatch changes
        setImages({
            main: skuByColor[currentColor]?.imageUrl || product.imageUrl || null,
            alt: skuByColor[currentColor]?.altImage || product.alternateImageUrls?.[0] || null
        })
    }, [ currentColor ])

    const handleSkuChange = (color) => {
        if(currentColor !== color) {
            setColorClicked(true);
            setCurrentColor(color);
        }
    }
    
    const showAllSwatches = (e) => {
        e.stopImmediatePropagation();
        setSwatchesDisplayed(colors.length);
    }

    return (
        <AutocompleteElement hit={product} as="li" class="ns-dropdown-product">
            <div
                data-url={product.url}
                className="ns-relative ns-w-full ns-flex ns-flex-col"
                data-nosto-element="product"
            >
                <div className="ns-dropdown-product__media ns-relative ns-w-full ns-overflow-hidden">
                    <button 
                        className={`ns-wishlist swym-button swym-add-to-wishlist-view-product product_${product.productId} swym-icon swym-heart ns-absolute`}
                        data-swaction="addToWishlist"
                        data-product-url={product.url}
                        data-product-id={product.productId}
                        data-variant-id={currentColor ? skuByColor[currentColor].id : product.skus?.[0]?.id}
                        aria-label="Add to Wishlist"
                        ns-wishlist-btn
                    />
                    <a href={currentColor ? `${product.url}?variant=${skuByColor[currentColor].id}` : product.url}>
                        {badge && 
                            <div class="ns-dropdown-product__badges ns-absolute ns-flex ns-flex-nowrap">
                                <span class="ns-dropdown-product__badge">{badge}</span>
                            </div>
                        }

                        <img
                            className="ns-dropdown-product__image ns-main-image ns-absolute ns-w-full ns-h-full"
                            srcset={getDropdownSrcset(images.main) ?? productImagePlaceholder}
                            src={images.main.replace('.jpg', '_640x.jpg').replace('.png', '_640x.png').replace('.webp', '_640x.webp') ?? productImagePlaceholder}
                            alt={product.name}
                            width="250"
                            height="250"
                            loading="lazy"
                        />
                        {images?.alt && images.alt !== '' && (
                            <img
                                className="ns-dropdown-product__image ns-alt-image ns-absolute ns-w-full ns-h-full"
                                srcset={getDropdownSrcset(images.alt)}
                                src={images.alt.replace('.jpg', '_640x.jpg').replace('.png', '_640x.png').replace('.webp', '_640x.webp')}
                                alt={product.name}
                                width="250"
                                height="250"
                                loading="lazy"
                            />
                        )}
                    </a>
                </div>
                <div className="ns-dropdown-product__metadata ns-flex ns-flex-col">
                    <a href={currentColor ? `${product.url}?variant=${skuByColor[currentColor].id}` : product.url} className="ns-dropdown-product__link">
                        <p className="ns-dropdown-product__title">{product.name}</p>
                        <div className="ns-dropdown-product__price-container ns-flex ns-flex-nowrap ns-items-center">
                            {!colorClicked && prices.range && prices.range?.lowest < prices.range?.highest ? (
                                <>
                                <span className="sr-only visually-hidden">Price Range:</span>
                                <span className="ns-dropdown-product__price">
                                    {format(prices.range.lowest).replace('.00', '')} to {format(prices.range.highest).replace('.00','')}
                                </span>
                                </>
                            ) : (
                                <>
                                {prices.listPrice && prices.listPrice > prices.price && (
                                    <>
                                    <span className="sr-only visually-hidden">Original Price:</span>
                                    <span className="ns-dropdown-product__compare-price">
                                        {format(prices.listPrice).replace('.00', '')}
                                    </span>
                                    </>
                                )}
                                <span className="sr-only visually-hidden">Final Price:</span>
                                <span className="ns-dropdown-product__price">{format(prices.price).replace('.00', '')}</span>
                                </>
                            )}
                        </div>
                    </a>
                    {(colors?.length > 1 && skuByColor) && (
                        <fieldset class="ns-dropdown-product__swatches-fieldset ns-w-full ns-flex ns-flex-wrap ns-items-center" aria-label="Select a color">
                            <legend className="sr-only visually-hidden">
                                Color: {skuByColor[currentColor]?.customFields?.find(field => field?.key === 'color')?.value}
                            </legend>
                            <div class="ns-dropdown-product__swatches-wrap ns-w-full ns-flex ns-flex-wrap ns-items-center">
                                {colors.slice(0, swatchesDisplayed).map(color => {
                                    const colorData = skuByColor[color];
                                    const colorName = colorData.customFields?.find(field => field.key === 'color')?.value;
                                    const swatchImage = colorData.customFields?.find(field => field.key === 'cql-swatch_image')?.value?.replace('--/', '--/w_50,h_50/');
                                    return (
                                        <label className="ns-dropdown-product__swatch ns-relative ns-flex ns-item-center ns-justify-center">
                                            <input
                                                className="ns-dropdown-product__swatch-input sr-only visually-hidden"
                                                name={`ns-swatch-${product.productId}`}
                                                type="radio"
                                                aria-label={colorName}
                                                checked={skuByColor[currentColor]?.id === colorData.id}
                                                value={colorData.id}
                                                onChange={() => handleSkuChange(color)}
                                            />
                                            <span className="sr-only visually-hidden">
                                                {colorData.name} - {colorData.availability === 'InStock' ? ' in stock' : 'out of stock'}
                                            </span>
                                            <img
                                                src={swatchImage}
                                                className="ns-dropdown-product__swatch-img"
                                                alt={colorName}
                                            />
                                        </label>
                                    )
                                })}
                                {(colors.length > 0 && swatchesDisplayed < colors.length) && (
                                    <button
                                        type="button"
                                        className="ns-dropdown-product__expand-swatches"
                                        onClick={showAllSwatches}
                                    >
                                        +{(colors.length - swatchesDisplayed)}
                                        <span> more</span>
                                    </button>
                                )}
                            </div>
                        </fieldset>
                    )}
                </div>
            </div>
        </AutocompleteElement>
    )
}